import React, { useEffect, useState } from "react";
import countries from "i18n-iso-countries";
import { usePost } from "../../hooks/usePost";
import { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import { ResponsiveChoroplethCanvas, ChoroplethTooltip } from "@nivo/geo";
import worldCountries from "./worldCountries.json"; // GeoJSON file
import { compassLightGreyBackground } from "../../styles";
import {
  Card,
  FlexibleContainer,
  RowContainer,
} from "../../components/containers/styledComponents";
import { CompassParagraph } from "../../components/common/styledComponents";
import {
  EngagementByGeo,
  StewardshipFilterParam,
} from "../../models/stewardshipModels";
import { LegendCircle } from "../../components/visuals/styledComponents";
import { ExportButton } from "../../components/buttons/ExportButton";
import { ENGAGEMENTS_BY_GEO_CONFIG } from "./constants";

const UNKNOWN_COLOR = "#e2e7e7";

export type GeoData = {
  id: string;
  value: number;
  countryName: string;
};

export const ChoroplethChart: React.FC<{ filters: StewardshipFilterParam }> = ({
  filters,
}) => {
  const [geoData, setGeoData] = useState<GeoData[]>([]);
  const {
    data,
    sendData: getEngagementsByGeo,
    isLoading,
  } = usePost<EngagementByGeo[], StewardshipFilterParam>();

  useEffect(() => {
    getEngagementsByGeo("GET_ENGAGEMENTS_BY_GEO", filters);
  }, [filters]);

  useEffect(() => {
    if (data) {
      const transformedData = data.map((item) => ({
        id: countries.alpha2ToAlpha3(item.geo_level_2) || item.geo_level_2,
        countryName:
          countries.getName(item.geo_level_2, "en") || item.geo_level_2,
        value: item.engagement_count,
      }));
      setGeoData(transformedData);
    }
  }, [data]);

  const customChoroplethTooltip = ({
    feature,
  }: {
    feature: {
      id: string;
      value: number;
      color: string;
      data: { id: string; value: number };
    };
  }): JSX.Element => {
    return (
      <div
        style={{
          background: "white",
          padding: "9px 12px",
          border: "1px solid #ccc",
          borderRadius: "7px",
        }}
      >
        <FlexibleContainer>
          <CompassParagraph bold={true}>Region:</CompassParagraph>
          <LegendCircle color={feature.color || UNKNOWN_COLOR} />
          <CompassParagraph>
            {countries.getName(feature.id, "en") || feature.id}
          </CompassParagraph>
        </FlexibleContainer>
        <CompassParagraph>
          <strong>Value:</strong> {feature.value || 0} engagements
        </CompassParagraph>
      </div>
    );
  };

  const countriesGeoJson = worldCountries as FeatureCollection<
    Geometry,
    GeoJsonProperties
  >;

  return (
    <Card h="470px" isLoading={isLoading} ovf="hidden">
      <RowContainer mb={-1}>
        <CompassParagraph fontSize={16} bold={true}>
          Engagements by geography
        </CompassParagraph>
      </RowContainer>
      <ResponsiveChoroplethCanvas
        data={geoData}
        features={countriesGeoJson.features}
        margin={{ top: 10, right: 0, bottom: 0, left: 0 }}
        colors={["#a3f7c1", "#20e290"]}
        domain={
          geoData
            ? [
                Math.min(...geoData.map((item) => item.value)),
                Math.max(...geoData.map((item) => item.value)),
              ]
            : [0, 0]
        }
        unknownColor={UNKNOWN_COLOR}
        label="properties.name"
        valueFormat=".0f"
        projectionScale={100}
        projectionTranslation={[0.5, 0.6]}
        projectionRotation={[0, 0, 0]}
        enableGraticule={false}
        borderWidth={1}
        borderColor={compassLightGreyBackground}
        tooltip={customChoroplethTooltip as unknown as ChoroplethTooltip}
        legends={[
          {
            anchor: "bottom-left",
            direction: "column",
            justify: true,
            translateX: 20,
            translateY: -60,
            itemsSpacing: 0,
            itemWidth: 92,
            itemHeight: 18,
            itemDirection: "left-to-right",
            itemOpacity: 0.85,
            symbolSize: 18,
          },
        ]}
      />
      {!isLoading && (
        <ExportButton data={geoData} {...ENGAGEMENTS_BY_GEO_CONFIG} />
      )}
    </Card>
  );
};
